import { useEffect, useMemo, useState } from "react";
import { db } from "./firebase";
import { collection, collectionGroup, getDocs, query } from "firebase/firestore";
import { useAuth } from "./AuthContext";
import "./DailyReport.css";
import { exportDailyReportToExcel } from "./utils/exportExcel";
import useTeamDirectory, { getTeamRealName, formatTeamWithRealName } from "./hooks/useTeamDirectory";
import {
  calculateRecordFee,
  canonicalTeamName,
  getBangkokDateParts,
} from "./utils/feeRules";
import { calcCompanyTopUp } from "./utils/payoutCalc";

const toDate = (value) => {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const bangkokDateParts = (value) => {
  const date = toDate(value);
  if (!date) return null;
  return getBangkokDateParts(date);
};

const isoDateKey = (value) => {
  const parts = bangkokDateParts(value);
  if (!parts) return "";
  return `${parts.year}-${String(parts.month).padStart(2, "0")}-${String(parts.day).padStart(2, "0")}`;
};

const displayDateKey = (value) => {
  const parts = bangkokDateParts(value);
  if (!parts) return "ไม่ระบุ";
  return `${String(parts.day).padStart(2, "0")}/${String(parts.month).padStart(2, "0")}/${parts.year + 543}`;
};

const parseDisplayDate = (value) => {
  const [day, month, buddhistYear] = String(value || "").split("/").map(Number);
  const year = buddhistYear > 2400 ? buddhistYear - 543 : buddhistYear;
  return year * 10000 + month * 100 + day;
};

const money = (value) =>
  Number(value || 0).toLocaleString("th-TH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const THAI_MONTHS = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
const number = (value) => Number(value || 0).toLocaleString("th-TH", { maximumFractionDigits: 2 });
const pct = (value) => `${Number(value || 0).toLocaleString("th-TH", { maximumFractionDigits: 1 })}%`;
const EXTRA_WHT_RATE = 0.03;
const roundMoney = (value) => Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
const extraAmounts = (item = {}) => {
  const whtRate = Number.isFinite(Number(item.whtRate)) ? Number(item.whtRate) : EXTRA_WHT_RATE;
  if (item.taxBorneByCompany) {
    const net = roundMoney(item.teamReceivesAmount ?? item.netAmount ?? item.amount ?? 0);
    const whtBase = roundMoney(item.whtBaseAmount ?? net);
    const wht = roundMoney(item.withholdingAmount ?? item.whtAmount ?? whtBase * whtRate);
    const gross = roundMoney(item.companyCostAmount ?? net + wht);
    return { gross, wht, net };
  }
  const gross = roundMoney(item.grossAmount ?? item.amount ?? 0);
  const wht = roundMoney(item.withholdingAmount ?? item.whtAmount ?? gross * whtRate);
  const net = roundMoney(item.netAmount ?? gross - wht);
  return { gross, wht, net };
};
const itemCreatedAt = (item) => item.createdAt || item.date || null;


const createAggregate = () => ({
  totalFeeSum: 0,
  totalTaxSum: 0,
  deductedFromTeam: 0,
  yyNet: 0,
  teamBeforeTaxExcludingYY: 0,
  teamNetExcludingYY: 0,
  storeIncome: 0,
  totalQty: 0,
  recordCount: 0,
  paidTeamNet: 0,
  unpaidTeamNet: 0,
  paidCount: 0,
  unpaidCount: 0,
});

const addRecordToAggregate = (acc, record) => {
  const effective = calculateRecordFee(record);
  const team = canonicalTeamName(record.teamName) || "ไม่ระบุ";
  acc.totalFeeSum += effective.baseFee;
  acc.totalTaxSum += effective.tax;
  acc.totalQty += Number(record.quantity || 0);
  acc.recordCount += 1;

  if (team === "ยย") {
    acc.yyNet += effective.net;
  } else {
    acc.deductedFromTeam += effective.deducted;
    acc.teamBeforeTaxExcludingYY += effective.teamBeforeTax;
    acc.teamNetExcludingYY += effective.net;
    if (record.isPaid) {
      acc.paidTeamNet += effective.net;
      acc.paidCount += 1;
    } else {
      acc.unpaidTeamNet += effective.net;
      acc.unpaidCount += 1;
    }
  }

  acc.storeIncome = acc.deductedFromTeam + acc.yyNet;
  return acc;
};

const aggregateRecords = (records = []) => records.reduce(addRecordToAggregate, createAggregate());

export default function DailyReport() {
  const { currentUser } = useAuth();
  const nowParts = getBangkokDateParts(new Date());

  const [records, setRecords] = useState([]);
  const [extraIncomes, setExtraIncomes] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [activeTab, setActiveTab] = useState("overview");

  const [selectedEvent, setSelectedEvent] = useState("all");
  const [selectedTeam, setSelectedTeam] = useState("all");
  const [selectedDate, setSelectedDate] = useState("all");
  const [selectedMonth, setSelectedMonth] = useState(String(nowParts.month));
  const [selectedYear, setSelectedYear] = useState(String(nowParts.year));
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const { realNameByTeam } = useTeamDirectory();

  useEffect(() => {
    let mounted = true;
    const fetchRecords = async () => {
      try {
        const [snapshot, extraSnapshot, settlementSnapshot] = await Promise.all([
          getDocs(query(collectionGroup(db, "records"))),
          getDocs(collection(db, "team-extra-income")),
          getDocs(collection(db, "team-payment-settlements")),
        ]);
        if (!mounted) return;
        setSettlements(settlementSnapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
        setRecords(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
        setExtraIncomes(extraSnapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      } catch (error) {
        console.error("Failed to load financial report", error);
        if (mounted) setLoadError("โหลดข้อมูลรายงานไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchRecords();
    return () => { mounted = false; };
  }, []);

  const eventNames = useMemo(() => {
    const latestByEvent = new Map();
    records.forEach((record) => {
      const name = record.eventName || "ไม่ระบุงาน";
      const time = toDate(record.createdAt)?.getTime() || 0;
      latestByEvent.set(name, Math.max(latestByEvent.get(name) || 0, time));
    });
    return [...latestByEvent.entries()].sort((a, b) => b[1] - a[1]).map(([name]) => name);
  }, [records]);

  const teamNames = useMemo(
    () => [...new Set(records.map((record) => canonicalTeamName(record.teamName) || "ไม่ระบุ"))]
      .sort((a, b) => a.localeCompare(b, "th")),
    [records]
  );

  const uniqueDates = useMemo(
    () => [...new Set(records.map((record) => displayDateKey(record.createdAt)))]
      .filter((date) => date !== "ไม่ระบุ")
      .sort((a, b) => parseDisplayDate(b) - parseDisplayDate(a)),
    [records]
  );

  const years = useMemo(
    () => [...new Set([
      ...records.map((record) => bangkokDateParts(record.createdAt)?.year).filter(Boolean),
      nowParts.year,
      2026,
      2027,
    ])].sort((a, b) => b - a),
    [records, nowParts.year]
  );

  const filteredRecords = useMemo(() => {
    return records
      .filter((record) => {
        const recordIso = isoDateKey(record.createdAt);
        if (dateFrom && (!recordIso || recordIso < dateFrom)) return false;
        if (dateTo && (!recordIso || recordIso > dateTo)) return false;
        return true;
      })
      .filter((record) => selectedEvent === "all" || (record.eventName || "ไม่ระบุงาน") === selectedEvent)
      .filter((record) => selectedTeam === "all" || (canonicalTeamName(record.teamName) || "ไม่ระบุ") === selectedTeam)
      .filter((record) => selectedDate === "all" || displayDateKey(record.createdAt) === selectedDate)
      .filter((record) => selectedMonth === "all" || bangkokDateParts(record.createdAt)?.month === Number(selectedMonth))
      .filter((record) => selectedYear === "all" || bangkokDateParts(record.createdAt)?.year === Number(selectedYear))
      .sort((a, b) => (toDate(b.createdAt)?.getTime() || 0) - (toDate(a.createdAt)?.getTime() || 0));
  }, [records, selectedEvent, selectedTeam, selectedDate, selectedMonth, selectedYear, dateFrom, dateTo]);

  const filteredExtras = useMemo(() => {
    // รายได้พิเศษไม่ได้ผูกกับ event จึงไม่นำไปหักเมื่อเจาะดูเฉพาะงาน
    if (selectedEvent !== "all") return [];
    return extraIncomes
      .filter((item) => {
        const createdAt = itemCreatedAt(item);
        const recordIso = isoDateKey(createdAt);
        if (dateFrom && (!recordIso || recordIso < dateFrom)) return false;
        if (dateTo && (!recordIso || recordIso > dateTo)) return false;
        return true;
      })
      .filter((item) => selectedTeam === "all" || (canonicalTeamName(item.teamName) || "ไม่ระบุ") === selectedTeam)
      .filter((item) => selectedDate === "all" || displayDateKey(itemCreatedAt(item)) === selectedDate)
      .filter((item) => selectedMonth === "all" || bangkokDateParts(itemCreatedAt(item))?.month === Number(selectedMonth))
      .filter((item) => selectedYear === "all" || bangkokDateParts(itemCreatedAt(item))?.year === Number(selectedYear));
  }, [extraIncomes, selectedEvent, selectedTeam, selectedDate, selectedMonth, selectedYear, dateFrom, dateTo]);

  // เงินช่วยจากบริษัทให้พนักงานเงินเดือนที่ทำยอดไม่ถึงเกณฑ์ (บันทึกในรอบโอนที่ยืนยันแล้ว)
  // นับเป็นค่าใช้จ่ายบริษัทในเดือนของรอบโอน (วันที่ 26)
  const filteredSupports = useMemo(() => {
    if (selectedEvent !== "all") return [];
    return settlements
      // โอนเกินจากที่ทีมทำได้ = บริษัทจ่ายเพิ่ม → คำนวณจากตัวเลขในรอบโอนทุกครั้ง (รวมรอบเก่า)
      .filter((item) => item.status === "paid")
      .map((item) => ({
        ...item,
        __topUp: calcCompanyTopUp(item).topUp,
        __date: item.settlementRoundDate || item.paidAt || item.createdAt,
      }))
      .filter((item) => item.__topUp > 0)
      .filter((item) => {
        const iso = isoDateKey(item.__date);
        if (dateFrom && (!iso || iso < dateFrom)) return false;
        if (dateTo && (!iso || iso > dateTo)) return false;
        return true;
      })
      .filter((item) => selectedTeam === "all" || (canonicalTeamName(item.teamName) || "ไม่ระบุ") === selectedTeam)
      .filter((item) => selectedDate === "all" || displayDateKey(item.__date) === selectedDate)
      .filter((item) => selectedMonth === "all" || bangkokDateParts(item.__date)?.month === Number(selectedMonth))
      .filter((item) => selectedYear === "all" || bangkokDateParts(item.__date)?.year === Number(selectedYear));
  }, [settlements, selectedEvent, selectedTeam, selectedDate, selectedMonth, selectedYear, dateFrom, dateTo]);

  // รายได้ร้านรายเดือนของปีที่เลือก (ทั้งร้าน ไม่ขึ้นกับตัวกรองเดือน/วัน/งาน/ทีม)
  // ใช้สูตรเดียวกับการ์ดด้านบน: กำไร = ส่วนแบ่งร้าน + ค่ากดของยย − รายได้พิเศษ(ต้นทุนบริษัท) − ส่วนที่บริษัทจ่ายเพิ่มให้ทีม
  const monthlyYear = selectedYear === "all" ? nowParts.year : Number(selectedYear);
  const monthlyRows = useMemo(() => {
    const rows = Array.from({ length: 12 }, (_, index) => ({
      month: index + 1,
      records: [],
      extraCost: 0,
      topUp: 0,
    }));
    records.forEach((record) => {
      const parts = bangkokDateParts(record.createdAt);
      if (parts?.year === monthlyYear) rows[parts.month - 1].records.push(record);
    });
    extraIncomes.forEach((item) => {
      const parts = bangkokDateParts(itemCreatedAt(item));
      if (parts?.year === monthlyYear) rows[parts.month - 1].extraCost += extraAmounts(item).gross;
    });
    settlements
      .filter((item) => item.status === "paid")
      .forEach((item) => {
        const parts = bangkokDateParts(item.settlementRoundDate || item.paidAt || item.createdAt);
        if (parts?.year === monthlyYear) rows[parts.month - 1].topUp += calcCompanyTopUp(item).topUp;
      });
    return rows.map((row) => {
      const agg = aggregateRecords(row.records);
      return {
        month: row.month,
        gross: agg.totalFeeSum,
        storeIncome: agg.storeIncome,
        profit: agg.storeIncome - row.extraCost - row.topUp,
        count: agg.recordCount,
      };
    });
  }, [records, extraIncomes, settlements, monthlyYear]);
  const monthlyMax = Math.max(1, ...monthlyRows.map((row) => row.profit));
  const bestMonth = monthlyRows.reduce((best, row) => (row.count > 0 && (!best || row.profit > best.profit) ? row : best), null);

  const sumAll = useMemo(() => {
    const base = aggregateRecords(filteredRecords);
    const extraIncomeNet = filteredExtras.reduce((sum, item) => sum + extraAmounts(item).net, 0);
    const extraIncomeWht = filteredExtras.reduce((sum, item) => sum + extraAmounts(item).wht, 0);
    const extraCompanyCost = filteredExtras.reduce((sum, item) => sum + extraAmounts(item).gross, 0);
    const salarySupport = filteredSupports.reduce((sum, item) => sum + Number(item.__topUp || 0), 0);
    return {
      ...base,
      extraIncomeNet,
      extraIncomeWht,
      extraCompanyCost,
      salarySupport,
      salarySupportCount: filteredSupports.length,
      profitAfterExtra: base.storeIncome - extraCompanyCost - salarySupport,
      extraIncomeCount: filteredExtras.length,
    };
  }, [filteredRecords, filteredExtras, filteredSupports]);

  const report = useMemo(() => {
    const groupedByDate = {};
    filteredRecords.forEach((record) => {
      const date = displayDateKey(record.createdAt);
      if (!groupedByDate[date]) groupedByDate[date] = [];
      groupedByDate[date].push(record);
    });

    const result = {};
    Object.entries(groupedByDate).forEach(([date, list]) => {
      const byTeam = {};
      const zoneSummary = {};

      list.forEach((record) => {
        const team = canonicalTeamName(record.teamName) || "ไม่ระบุ";
        const effective = calculateRecordFee(record);
        if (!byTeam[team]) {
          byTeam[team] = {
            name: team,
            total: 0,
            deducted: 0,
            beforeTax: 0,
            count: 0,
            tax: 0,
            net: 0,
            records: 0,
          };
        }
        byTeam[team].total += effective.baseFee;
        byTeam[team].deducted += effective.deducted;
        byTeam[team].beforeTax += effective.teamBeforeTax;
        byTeam[team].count += Number(record.quantity || 0);
        byTeam[team].tax += effective.tax;
        byTeam[team].net += effective.net;
        byTeam[team].records += 1;

        const zone = record.zone || record.priceZone || record.label || "ไม่ระบุโซน";
        zoneSummary[zone] = (zoneSummary[zone] || 0) + Number(record.quantity || 0);
      });

      result[date] = {
        teams: Object.values(byTeam).sort((a, b) => b.net - a.net),
        zoneSummary,
        ...aggregateRecords(list),
      };
    });
    return result;
  }, [filteredRecords]);

  const eventRows = useMemo(() => {
    const map = new Map();
    filteredRecords.forEach((record) => {
      const name = record.eventName || "ไม่ระบุงาน";
      const current = map.get(name) || { name, records: [] };
      current.records.push(record);
      map.set(name, current);
    });
    return [...map.values()]
      .map((row) => {
        const sums = aggregateRecords(row.records);
        return {
          ...row,
          ...sums,
          margin: sums.totalFeeSum > 0 ? (sums.storeIncome / sums.totalFeeSum) * 100 : 0,
        };
      })
      .sort((a, b) => b.storeIncome - a.storeIncome);
  }, [filteredRecords]);

  const teamRows = useMemo(() => {
    const map = new Map();
    filteredRecords.forEach((record) => {
      const name = canonicalTeamName(record.teamName) || "ไม่ระบุ";
      const current = map.get(name) || {
        name,
        qty: 0,
        count: 0,
        gross: 0,
        shopIncome: 0,
        beforeWht: 0,
        wht: 0,
        net: 0,
      };
      const fee = calculateRecordFee(record);
      current.qty += Number(record.quantity || 0);
      current.count += 1;
      current.gross += fee.baseFee;
      current.beforeWht += fee.teamBeforeTax;
      current.wht += fee.tax;
      current.net += fee.net;
      current.shopIncome += name === "ยย" ? fee.net : fee.deducted;
      map.set(name, current);
    });
    return [...map.values()].sort((a, b) => b.shopIncome - a.shopIncome);
  }, [filteredRecords]);

  const isThisMonth =
    selectedMonth === String(nowParts.month) && selectedYear === String(nowParts.year) &&
    selectedDate === "all" && !dateFrom && !dateTo;
  const isShowingAll =
    selectedMonth === "all" && selectedYear === "all" && selectedEvent === "all" &&
    selectedTeam === "all" && selectedDate === "all" && !dateFrom && !dateTo;
  const periodLabel = [
    dateFrom || dateTo
      ? `${dateFrom || "…"} ถึง ${dateTo || "…"}`
      : selectedDate !== "all"
        ? `วันที่ ${selectedDate}`
        : `${selectedMonth === "all" ? "ทุกเดือน" : THAI_MONTHS[Number(selectedMonth) - 1]} ${selectedYear === "all" ? "ทุกปี" : Number(selectedYear) + 543}`,
    selectedTeam !== "all" ? `ทีม ${selectedTeam}` : null,
    selectedEvent !== "all" ? selectedEvent : null,
  ].filter(Boolean).join(" · ");
  const visibleTeamCount = teamRows.length;
  const visibleEventCount = eventRows.length;
  const grossMargin = sumAll.totalFeeSum > 0 ? (sumAll.storeIncome / sumAll.totalFeeSum) * 100 : 0;

  const clearCustomDates = () => {
    setDateFrom("");
    setDateTo("");
  };

  const showThisMonth = () => {
    setSelectedDate("all");
    setSelectedMonth(String(nowParts.month));
    setSelectedYear(String(nowParts.year));
    clearCustomDates();
  };

  const showAll = () => {
    setSelectedEvent("all");
    setSelectedTeam("all");
    setSelectedDate("all");
    setSelectedMonth("all");
    setSelectedYear("all");
    clearCustomDates();
  };

  const onSelectedDateChange = (value) => {
    setSelectedDate(value);
    if (value !== "all") {
      setSelectedMonth("all");
      setSelectedYear("all");
      clearCustomDates();
    }
  };

  const onCustomDateChange = (setter) => (event) => {
    setter(event.target.value);
    setSelectedDate("all");
    setSelectedMonth("all");
    setSelectedYear("all");
  };

  const handleExportExcel = () => {
    exportDailyReportToExcel({
      records: filteredRecords,
      report,
      sumAll,
      filters: {
        selectedEvent,
        selectedTeam,
        selectedDate,
        selectedMonth,
        selectedYear,
        dateFrom,
        dateTo,
      },
      realNameByTeam,
      calcEffective: calculateRecordFee,
      title: "การเงินและรายงานค่ากด - ยยมือทองกดบัตร",
    });
  };

  if (!currentUser || !currentUser.email || currentUser.email !== "notloser.08@gmail.com") {
    return <p className="dr-access-denied">⛔️ ไม่มีสิทธิ์เข้าถึงหน้านี้</p>;
  }

  const reportEntries = Object.entries(report).sort(
    ([dateA], [dateB]) => parseDisplayDate(dateB) - parseDisplayDate(dateA)
  );

  return (
    <div className="dr-page">
      <header className="dr-hero dr-hero--compact">
        <div>
          <span className="dr-eyebrow">FINANCE & REPORT</span>
          <h1>การเงิน & รายงาน</h1>
          <p className="dr-period">{periodLabel}</p>
        </div>
        <button
          className="dr-export-btn"
          type="button"
          onClick={handleExportExcel}
          disabled={loading || filteredRecords.length === 0}
        >
          <span>↓</span> ส่งออก Excel
        </button>
      </header>

      {/* ตัวกรอง: แถวเดียว + ตัวกรองละเอียดพับเก็บ */}
      <section className="dr-toolbar">
        <div className="dr-toolbar-quick">
          <button type="button" className={isThisMonth ? "is-active" : ""} onClick={showThisMonth}>เดือนนี้</button>
          <button type="button" className={isShowingAll ? "is-active" : ""} onClick={showAll}>ทั้งหมด</button>
        </div>
        <div className="dr-toolbar-selects">
          <FilterSelect
            label="ปี"
            value={selectedYear}
            onChange={(value) => {
              setSelectedYear(value);
              setSelectedDate("all");
              clearCustomDates();
            }}
          >
            <option value="all">ทุกปี</option>
            {years.map((year) => <option key={year} value={String(year)}>{year + 543}</option>)}
          </FilterSelect>
          <FilterSelect
            label="เดือน"
            value={selectedMonth}
            onChange={(value) => {
              setSelectedMonth(value);
              setSelectedDate("all");
              clearCustomDates();
            }}
          >
            <option value="all">ทุกเดือน</option>
            {THAI_MONTHS.map((name, index) => (
              <option key={name} value={String(index + 1)}>{name}</option>
            ))}
          </FilterSelect>
          <FilterSelect label="ทีม" value={selectedTeam} onChange={setSelectedTeam}>
            <option value="all">ทุกทีม</option>
            {teamNames.map((name) => <option key={name} value={name}>{formatTeamWithRealName(name, realNameByTeam)}</option>)}
          </FilterSelect>
          <FilterSelect label="งาน" value={selectedEvent} onChange={setSelectedEvent}>
            <option value="all">ทุกงาน</option>
            {eventNames.map((name) => <option key={name} value={name}>{name}</option>)}
          </FilterSelect>
        </div>
        <details className="dr-toolbar-more" open={selectedDate !== "all" || !!dateFrom || !!dateTo}>
          <summary>เลือกวัน / ช่วงวันที่</summary>
          <div className="dr-toolbar-more-body">
            <FilterSelect label="วันที่" value={selectedDate} onChange={onSelectedDateChange}>
              <option value="all">ทุกวัน</option>
              {uniqueDates.map((date) => <option key={date} value={date}>{date}</option>)}
            </FilterSelect>
            <label className="dr-filter-field">
              <span>ตั้งแต่</span>
              <input type="date" value={dateFrom} onChange={onCustomDateChange(setDateFrom)} />
            </label>
            <label className="dr-filter-field">
              <span>ถึง</span>
              <input type="date" value={dateTo} onChange={onCustomDateChange(setDateTo)} />
            </label>
            <button type="button" className="dr-clear-range" onClick={clearCustomDates} disabled={!dateFrom && !dateTo}>
              ล้างช่วงวันที่
            </button>
          </div>
        </details>
      </section>

      {/* กำไร: ตัวเลขหลัก + ที่มาของกำไรแบบไล่บรรทัด */}
      <section className="dr-overview">
        <div className="dr-profit-hero">
          <span className="dr-profit-hero-label">กำไรหลังหักค่าใช้จ่ายพิเศษ</span>
          <strong className="dr-profit-hero-value">{money(sumAll.profitAfterExtra)} ฿</strong>
          <div className="dr-waterfall">
            <div><span>ส่วนแบ่งร้านจากทีม</span><b>+{money(sumAll.deductedFromTeam)}</b></div>
            <div><span>ค่ากดของยย</span><b>+{money(sumAll.yyNet)}</b></div>
            <div className="is-subtotal"><span>กำไรจากค่ากด</span><b>{money(sumAll.storeIncome)}</b></div>
            {sumAll.extraIncomeNet > 0 && <div className="is-minus"><span>รายได้พิเศษที่จ่ายทีม</span><b>−{money(sumAll.extraIncomeNet)}</b></div>}
            {sumAll.extraIncomeWht > 0 && <div className="is-minus"><span>WHT ของรายได้พิเศษ</span><b>−{money(sumAll.extraIncomeWht)}</b></div>}
            {sumAll.salarySupport > 0 && <div className="is-minus"><span>บริษัทจ่ายเพิ่มให้ทีม</span><b>−{money(sumAll.salarySupport)}</b></div>}
            <div className="is-total"><span>กำไรหลังหักค่าใช้จ่ายพิเศษ</span><b>{money(sumAll.profitAfterExtra)}</b></div>
          </div>
        </div>

        <div className="dr-overview-side">
          <div className="dr-stat">
            <span>ค่ากดรวม</span>
            <strong>{money(sumAll.totalFeeSum)} ฿</strong>
            <small>{number(visibleEventCount)} งาน · {number(sumAll.recordCount)} รายการ · {number(sumAll.totalQty)} ใบ</small>
          </div>
          <div className="dr-stat">
            <span>สัดส่วนรายได้ร้าน</span>
            <strong>{pct(grossMargin)}</strong>
            <small>กำไรจากค่ากด ÷ ค่ากดรวม</small>
          </div>
          <div className="dr-stat dr-stat--team">
            <span>เงินของทีม ({number(visibleTeamCount)} ทีม)</span>
            <strong>{money(sumAll.teamNetExcludingYY)} ฿</strong>
            <div className="dr-paid-bar" aria-hidden="true">
              <span style={{ width: `${sumAll.teamNetExcludingYY > 0 ? Math.min(100, (sumAll.paidTeamNet / sumAll.teamNetExcludingYY) * 100) : 0}%` }} />
            </div>
            <div className="dr-paid-legend">
              <span className="is-paid">โอนแล้ว {money(sumAll.paidTeamNet)}</span>
              <span className="is-unpaid">ค้างจ่าย {money(sumAll.unpaidTeamNet)}</span>
            </div>
          </div>
        </div>
      </section>

      <section className="dr-monthly-card">
        <div className="dr-monthly-head">
          <div>
            <span className="dr-section-kicker">MONTHLY</span>
            <h2>กำไรรายเดือน · ปี {monthlyYear + 543}</h2>
            <p>ทั้งร้าน · กดที่เดือนเพื่อดูรายละเอียด</p>
          </div>
          {bestMonth && (
            <div className="dr-monthly-best">
              <span>เดือนที่ดีที่สุด</span>
              <strong>{THAI_MONTHS[bestMonth.month - 1]}</strong>
              <small>{money(bestMonth.profit)} ฿</small>
            </div>
          )}
        </div>
        <div className="dr-monthly-bars">
          {monthlyRows.map((row) => (
            <button
              type="button"
              key={row.month}
              className={`dr-monthly-bar ${bestMonth?.month === row.month ? "is-best" : ""} ${String(row.month) === selectedMonth ? "is-selected" : ""} ${row.count === 0 ? "is-empty" : ""}`}
              onClick={() => { setSelectedYear(String(monthlyYear)); setSelectedMonth(String(row.month)); setSelectedDate("all"); clearCustomDates(); }}
              title={`${THAI_MONTHS[row.month - 1]} · กำไร ${money(row.profit)} ฿ · ค่ากดรวม ${money(row.gross)} ฿ · ${number(row.count)} รายการ`}
            >
              <span className="dr-monthly-value">{row.count ? `${number(Math.round(row.profit / 1000))}k` : "–"}</span>
              <span className="dr-monthly-track"><span style={{ height: `${Math.max(0, (row.profit / monthlyMax) * 100)}%` }} /></span>
              <span className="dr-monthly-label">{THAI_MONTHS[row.month - 1]}</span>
            </button>
          ))}
        </div>
      </section>

      <p className="dr-footnote">
        ⓘ กำไรนี้นับตามวันที่เกิดรายการ ยังไม่ใช่กำไรสุทธิทางบัญชี — ยังไม่รวมเงินเดือนฐาน ประกันสังคมส่วนนายจ้าง อุปกรณ์ ซอฟต์แวร์ และค่าใช้จ่ายบริษัทอื่น · ยอดค้างจ่ายทีมเป็นหนี้ที่ต้องจ่าย ไม่ใช่กำไร
      </p>

      <nav className="dr-tabs" aria-label="มุมมองรายงาน">
        <button className={activeTab === "overview" ? "is-active" : ""} onClick={() => setActiveTab("overview")}>ภาพรวมการเงิน</button>
        <button className={activeTab === "events" ? "is-active" : ""} onClick={() => setActiveTab("events")}>แยกตามงาน</button>
        <button className={activeTab === "transactions" ? "is-active" : ""} onClick={() => setActiveTab("transactions")}>รายละเอียดทั้งหมด</button>
      </nav>

      {loading ? (
        <div className="dr-state-card">
          <span className="dr-loader" />
          <strong>กำลังโหลดรายงาน</strong>
          <p>กำลังรวบรวมข้อมูลค่ากดทั้งหมด</p>
        </div>
      ) : loadError ? (
        <div className="dr-state-card dr-state-card--error"><strong>{loadError}</strong></div>
      ) : filteredRecords.length === 0 ? (
        <div className="dr-state-card">
          <div className="dr-empty-icon">⌕</div>
          <strong>ไม่พบข้อมูลในช่วงที่เลือก</strong>
          <p>ลองเปลี่ยนตัวกรอง หรือกด “ดูทั้งหมด”</p>
        </div>
      ) : activeTab === "overview" ? (
        <FinanceOverview eventRows={eventRows} teamRows={teamRows} realNameByTeam={realNameByTeam} />
      ) : activeTab === "events" ? (
        <EventBreakdown rows={eventRows} realNameByTeam={realNameByTeam} />
      ) : (
        <TransactionDetail reportEntries={reportEntries} filteredCount={filteredRecords.length} realNameByTeam={realNameByTeam} />
      )}
    </div>
  );
}

function FinanceOverview({ eventRows, teamRows, realNameByTeam }) {
  const topEvents = eventRows.slice(0, 6);
  const topTeams = teamRows.slice(0, 6);
  const maxEvent = topEvents[0]?.storeIncome || 0;
  const maxTeam = topTeams[0]?.shopIncome || 0;

  return (
    <div className="dr-overview-grid">
      <section className="dr-insight-card">
        <div className="dr-insight-head">
          <div>
            <span className="dr-section-kicker">TOP EVENTS</span>
            <h2>งานที่สร้างกำไรขั้นต้นให้ร้าน</h2>
          </div>
          <span>{eventRows.length} งาน</span>
        </div>
        <div className="dr-insight-list">
          {topEvents.map((row, index) => (
            <div className="dr-insight-row" key={row.name}>
              <div className="dr-insight-rank">{index + 1}</div>
              <div className="dr-insight-main">
                <div><strong>{row.name}</strong><span>{number(row.totalQty)} ใบ · {number(row.recordCount)} รายการ</span></div>
                <div className="dr-mini-progress"><i style={{ width: `${maxEvent > 0 ? Math.max(4, row.storeIncome / maxEvent * 100) : 0}%` }} /></div>
              </div>
              <div className="dr-insight-value"><small>กำไรขั้นต้น</small><strong>{money(row.storeIncome)} ฿</strong></div>
            </div>
          ))}
        </div>
      </section>

      <section className="dr-insight-card">
        <div className="dr-insight-head">
          <div>
            <span className="dr-section-kicker">SHOP CONTRIBUTION</span>
            <h2>ใครสร้างรายได้ให้ร้าน</h2>
          </div>
          <span>{teamRows.length} ทีม</span>
        </div>
        <div className="dr-insight-list">
          {topTeams.map((row, index) => (
            <div className="dr-insight-row" key={row.name}>
              <div className="dr-insight-rank">{index + 1}</div>
              <div className="dr-insight-main">
                <div>
                  <strong>{row.name}</strong>
                  {getTeamRealName(row.name, realNameByTeam) && <small className="dr-team-real-name">{getTeamRealName(row.name, realNameByTeam)}</small>}
                  <span>{row.name === "ยย" ? "ค่ากดเจ้าของร้าน" : `ค่ากดรวม ${money(row.gross)} ฿`}</span>
                </div>
                <div className="dr-mini-progress"><i style={{ width: `${maxTeam > 0 ? Math.max(4, row.shopIncome / maxTeam * 100) : 0}%` }} /></div>
              </div>
              <div className="dr-insight-value"><small>ร้านได้</small><strong>{money(row.shopIncome)} ฿</strong></div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function EventBreakdown({ rows, realNameByTeam }) {
  const [expandedEvent, setExpandedEvent] = useState(null);
  const [expandedZone, setExpandedZone] = useState(null);

  const toggleEvent = (name) => {
    setExpandedEvent((current) => current === name ? null : name);
    setExpandedZone(null);
  };

  const toggleZone = (eventName, zoneName) => {
    const key = `${eventName}::${zoneName}`;
    setExpandedZone((current) => current === key ? null : key);
  };

  return (
    <section className="dr-event-drilldown">
      <div className="dr-section-head">
        <div>
          <h2>รายละเอียดแยกตามงาน</h2>
          <p>กดที่งานเพื่อดูโซน แล้วกดที่โซนเพื่อดูทีมและรายการจริง</p>
        </div>
        <span className="dr-section-count">{number(rows.length)} งาน</span>
      </div>

      <div className="dr-event-accordion">
        {rows.map((row) => {
          const isOpen = expandedEvent === row.name;
          const zones = buildZoneBreakdown(row.records);

          return (
            <article className={`dr-event-card ${isOpen ? "is-open" : ""}`} key={row.name}>
              <button className="dr-event-card-head" type="button" onClick={() => toggleEvent(row.name)}>
                <div className="dr-event-card-title">
                  <span className="dr-event-caret">{isOpen ? "−" : "+"}</span>
                  <div>
                    <strong>{row.name}</strong>
                    <small>{number(row.totalQty)} ใบ · {number(row.recordCount)} รายการ · {number(zones.length)} โซน</small>
                  </div>
                </div>
                <div className="dr-event-card-metrics">
                  <Metric label="ค่ากดรวม" value={`${money(row.totalFeeSum)} ฿`} />
                  <Metric label="กำไรขั้นต้นร้าน" value={`${money(row.storeIncome)} ฿`} accent />
                  <Metric label="จ่ายทีม" value={`${money(row.teamNetExcludingYY)} ฿`} />
                  <span className="dr-margin-pill">{pct(row.margin)}</span>
                </div>
              </button>

              {isOpen && (
                <div className="dr-event-card-body">
                  <div className="dr-event-finance-strip">
                    <div><span>ส่วนแบ่งร้านจากทีม</span><strong>{money(row.deductedFromTeam)} ฿</strong></div>
                    <div><span>ค่ากดของยย</span><strong>{money(row.yyNet)} ฿</strong></div>
                    <div><span>WHT</span><strong>{money(row.totalTaxSum)} ฿</strong></div>
                    <div><span>Margin ร้าน</span><strong>{pct(row.margin)}</strong></div>
                  </div>

                  <div className="dr-zone-breakdown-head">
                    <div>
                      <span className="dr-section-kicker">ZONE BREAKDOWN</span>
                      <h3>โซนที่กดได้</h3>
                    </div>
                    <span>กดแต่ละโซนเพื่อดูว่าใครกดและได้กี่ใบ</span>
                  </div>

                  <div className="dr-zone-accordion">
                    {zones.map((zone) => {
                      const zoneKey = `${row.name}::${zone.name}`;
                      const zoneOpen = expandedZone === zoneKey;
                      return (
                        <div className={`dr-zone-card ${zoneOpen ? "is-open" : ""}`} key={zone.name}>
                          <button className="dr-zone-card-head" type="button" onClick={() => toggleZone(row.name, zone.name)}>
                            <div className="dr-zone-name-block">
                              <span className="dr-zone-caret">{zoneOpen ? "−" : "+"}</span>
                              <div>
                                <strong>{zone.name}</strong>
                                <small>{number(zone.totalQty)} ใบ · {number(zone.recordCount)} รายการ · {number(zone.teamCount)} ทีม</small>
                              </div>
                            </div>
                            <div className="dr-zone-summary-metrics">
                              <div><span>ค่ากด</span><strong>{money(zone.totalFeeSum)} ฿</strong></div>
                              <div><span>ร้านได้</span><strong>{money(zone.storeIncome)} ฿</strong></div>
                            </div>
                          </button>

                          {zoneOpen && (
                            <div className="dr-zone-card-body">
                              <div className="dr-zone-team-grid">
                                {zone.teams.map((team) => (
                                  <div className="dr-zone-team-card" key={team.name}>
                                    <div>
                                      <span className="dr-team-avatar">{team.name.slice(0, 2)}</span>
                                      <div>
                                        <strong>{team.name}</strong>
                                        {getTeamRealName(team.name, realNameByTeam) && <small className="dr-team-real-name">{getTeamRealName(team.name, realNameByTeam)}</small>}
                                        <small>{number(team.recordCount)} รายการ</small>
                                      </div>
                                    </div>
                                    <div className="dr-zone-team-numbers">
                                      <span>{number(team.qty)} ใบ</span>
                                      <strong>{money(team.net)} ฿</strong>
                                      <small>ร้านได้ {money(team.shopIncome)} ฿</small>
                                    </div>
                                  </div>
                                ))}
                              </div>

                              <div className="dr-zone-records">
                                <div className="dr-zone-records-title">
                                  <strong>รายการในโซนนี้</strong>
                                  <span>{number(zone.recordCount)} รายการ</span>
                                </div>
                                <div className="dr-zone-record-list">
                                  {zone.records.map((record) => {
                                    const fee = calculateRecordFee(record);
                                    const team = canonicalTeamName(record.teamName) || "ไม่ระบุ";
                                    const shopIncome = team === "ยย" ? fee.net : fee.deducted;
                                    return (
                                      <div className="dr-zone-record-row" key={`${record.id}-${isoDateKey(record.createdAt)}`}>
                                        <div className="dr-zone-record-main">
                                          <strong>{team}</strong>
                                          {getTeamRealName(team, realNameByTeam) && <small className="dr-team-real-name">{getTeamRealName(team, realNameByTeam)}</small>}
                                          <span>{displayDateKey(record.createdAt)}</span>
                                        </div>
                                        <div><span>จำนวน</span><strong>{number(record.quantity || 0)} ใบ</strong></div>
                                        <div><span>ค่ากด</span><strong>{money(fee.baseFee)} ฿</strong></div>
                                        <div><span>ร้านได้</span><strong className="profit">{money(shopIncome)} ฿</strong></div>
                                        <div><span>ทีมสุทธิ</span><strong>{money(fee.net)} ฿</strong></div>
                                        <div><span>สถานะ</span><strong className={team === "ยย" || record.isPaid ? "paid" : "unpaid"}>{team === "ยย" ? "ไม่ต้องโอน" : record.isPaid ? "โอนแล้ว" : "รอโอน"}</strong></div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}

function Metric({ label, value, accent = false }) {
  return (
    <span className={`dr-event-metric ${accent ? "is-accent" : ""}`}>
      <small>{label}</small>
      <strong>{value}</strong>
    </span>
  );
}

function buildZoneBreakdown(records = []) {
  const map = new Map();

  records.forEach((record) => {
    const zoneName = record.zone || record.priceZone || record.label || "ไม่ระบุโซน";
    const current = map.get(zoneName) || { name: zoneName, records: [] };
    current.records.push(record);
    map.set(zoneName, current);
  });

  return [...map.values()]
    .map((zone) => {
      const sums = aggregateRecords(zone.records);
      const teamMap = new Map();

      zone.records.forEach((record) => {
        const teamName = canonicalTeamName(record.teamName) || "ไม่ระบุ";
        const fee = calculateRecordFee(record);
        const current = teamMap.get(teamName) || {
          name: teamName,
          qty: 0,
          recordCount: 0,
          gross: 0,
          net: 0,
          shopIncome: 0,
        };
        current.qty += Number(record.quantity || 0);
        current.recordCount += 1;
        current.gross += fee.baseFee;
        current.net += fee.net;
        current.shopIncome += teamName === "ยย" ? fee.net : fee.deducted;
        teamMap.set(teamName, current);
      });

      return {
        ...zone,
        ...sums,
        teamCount: teamMap.size,
        teams: [...teamMap.values()].sort((a, b) => b.qty - a.qty || b.net - a.net),
        records: [...zone.records].sort(
          (a, b) => (toDate(b.createdAt)?.getTime() || 0) - (toDate(a.createdAt)?.getTime() || 0)
        ),
      };
    })
    .sort((a, b) => b.totalQty - a.totalQty || b.storeIncome - a.storeIncome);
}

function TransactionDetail({ reportEntries, filteredCount, realNameByTeam }) {
  return (
    <>
      <div className="dr-section-head">
        <div>
          <h2>รายละเอียดทั้งหมด</h2>
          <p>{number(reportEntries.length)} วัน · {number(filteredCount)} รายการตามตัวกรอง</p>
        </div>
      </div>
      <div className="dr-day-list">
        {reportEntries.map(([date, data]) => (
          <article className="dr-day-card" key={date}>
            <div className="dr-day-head">
              <div>
                <span className="dr-day-date">{date}</span>
                <div className="dr-day-meta">
                  <span>{number(data.teams.length)} ทีม</span><span>•</span>
                  <span>{number(data.totalQty)} ใบ</span><span>•</span>
                  <span>{number(data.recordCount)} รายการ</span>
                </div>
              </div>
              <div className="dr-day-income">
                <span>กำไรขั้นต้นวันนี้</span>
                <strong>{money(data.storeIncome)} ฿</strong>
              </div>
            </div>

            <div className="dr-table-wrap">
              <table className="dr-table">
                <thead>
                  <tr>
                    <th>ทีม</th>
                    <th className="center">จำนวนใบ</th>
                    <th className="right">ค่ากดรวม</th>
                    <th className="right">หักค่าคอม</th>
                    <th className="right">ก่อน WHT</th>
                    <th className="right">WHT</th>
                    <th className="right">ทีมได้รับสุทธิ</th>
                  </tr>
                </thead>
                <tbody>
                  {data.teams.map((team) => (
                    <tr key={team.name}>
                      <td data-label="ทีม">
                        <div className="dr-team-cell">
                          <span className="dr-team-avatar">{team.name.slice(0, 2)}</span>
                          <div>
                            <strong>{team.name}</strong>
                            {getTeamRealName(team.name, realNameByTeam) && <small className="dr-team-real-name">{getTeamRealName(team.name, realNameByTeam)}</small>}
                            <small>{number(team.records)} รายการ</small>
                          </div>
                        </div>
                      </td>
                      <td data-label="จำนวนใบ" className="center">{number(team.count)}</td>
                      <td data-label="ค่ากดรวม" className="right">{money(team.total)}</td>
                      <td data-label="หักค่าคอม" className="right deduction">−{money(team.deducted)}</td>
                      <td data-label="ก่อน WHT" className="right">{money(team.beforeTax)}</td>
                      <td data-label="WHT" className="right deduction">−{money(team.tax)}</td>
                      <td data-label="ทีมได้รับสุทธิ" className="right net">{money(team.net)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="dr-zone-row">
              <span className="dr-zone-title">โซน</span>
              <div className="dr-zone-chips">
                {Object.entries(data.zoneSummary).sort((a, b) => b[1] - a[1]).map(([zone, count]) => (
                  <span key={zone}>{zone} <b>{number(count)}</b></span>
                ))}
              </div>
            </div>

            <div className="dr-day-footer dr-day-footer--four">
              <div><span>ค่ากดรวม</span><strong>{money(data.totalFeeSum)} ฿</strong></div>
              <div><span>ส่วนแบ่งร้าน + ยย</span><strong>{money(data.storeIncome)} ฿</strong></div>
              <div><span>จ่ายทีมสุทธิ</span><strong>{money(data.teamNetExcludingYY)} ฿</strong></div>
              <div><span>WHT</span><strong>{money(data.totalTaxSum)} ฿</strong></div>
            </div>
          </article>
        ))}
      </div>
    </>
  );
}

function FilterSelect({ label, value, onChange, children }) {
  return (
    <label className="dr-filter-field">
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>{children}</select>
    </label>
  );
}
